<template>
  <a-layout-content class="my-4 mx-3">
    <div class="container">
      <!-- HEADER -->
      <div class="d-flex justify-content-between align-items-start">
        <h2>Week Schedule</h2>
        <div class="d-flex align-items-center">
          <a-radio-group v-model="viewMode" button-style="solid" class="mr-2">
            <a-radio-button value="table"> <a-icon type="table" /> Table </a-radio-button>
            <a-radio-button value="timeline"> <a-icon type="calendar" /> Timeline </a-radio-button>
          </a-radio-group>
          <portal-target name="week-schedule-filter-buttons"></portal-target>
        </div>
      </div>

      <!-- SCHEDULE FILTER -->
      <week-schedule-filter
        :profile="profile"
        :filters="filtersRef"
        :number-of-results="schedulePagination?.total"
        @apply-filters="(filters) => applyFilters(filters)"
      />

      <!-- SCHEDULE VIEWS -->
      <week-schedule-user-list
        v-if="viewMode === 'table'"
        :schedule-page="schedulePage"
        :current-page="currentPage"
        :page-size="pageSizeRef"
        :loading-table="loadingTable"
        :schedule-pagination="schedulePagination"
        :sortable="true"
        :profile="profile"
        :filters="filtersRef"
        :acl="baseAcl"
        @apply-sorting="(sorting) => applySorting(sorting)"
        @get-schedule-page="(params) => getSchedulePage(params)"
      />

      <week-schedule-timeline
        v-else
        :schedule-page="schedulePage"
        :absence-reports="absenceReports"
        :public-holidays="publicHolidays"
        :pending-role-extensions="pendingRoleExtensions"
        :current-page="currentPage"
        :page-size="pageSizeRef"
        :loading-table="loadingTable"
        :schedule-pagination="schedulePagination"
        @get-schedule-page="(params) => getSchedulePage(params)"
        @visible-years-change="getPublicHolidays"
      />
    </div>
  </a-layout-content>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed, getCurrentInstance } from "vue";
import { BaseAclInterface, ProfileInterface } from "@/modules/authentication/types";
import WeekScheduleUserList from "../_components/WeekScheduleUserList.vue";
import WeekScheduleTimeline from "../_components/WeekScheduleTimeline.vue";
import { useBookmarkedFiltersStore } from "@/modules/bookmarked_filters/_store";
import WeekScheduleFilter from "../_components/WeekScheduleFilter.vue";
import { useAuthenticationStore } from "@/modules/authentication/_store";
import { useWeekScheduleStore } from "@/modules/week_schedule/_store";
import { apiGetRoleExtensions } from "@/modules/roles/_utils/api";
import { RoleExtensionInterface } from "@/modules/roles/types";
import { apiGetAnnualPublicHolidays } from "@/modules/reports/_utils/api";
import { PublicHolidayRangeInterface, ReportInterface } from "@/modules/reports/types";
import moment from "@/date";
import {
  WeekScheduleFilterInterface,
  WeekSchedulePendingRoleExtensionInterface,
  WeekScheduleUserInterface,
} from "../types";
import { PaginationInterface } from "@/common/types";
import useMixin from "@/useMixin";
import { apiGetAbsenceReportsAsync } from "../_utils/api";
// Pinia
const authenticationStore = useAuthenticationStore();
const bookmarkedFiltersStore = useBookmarkedFiltersStore();
const weekScheduleStore = useWeekScheduleStore();
const profile = computed<ProfileInterface | undefined>(() => authenticationStore.profile);
const baseAcl = computed<BaseAclInterface>(() => authenticationStore.baseAcl);
const schedulePagination = computed<PaginationInterface | undefined>(
  () => weekScheduleStore.schedulePagination
);

// Mixins
const { qsDecode, loadTablePageSize, syncRouteParams, persistTablePageSize } = useMixin();

// Instances
const instance = getCurrentInstance();
const $route = instance?.proxy?.$route;

// Data properties
const filtersRef = ref<WeekScheduleFilterInterface>({
  employment_type: ["fixed", "variable", "subcontractor"],
});
const loadingTable = ref<boolean>(false);
const schedulePage = ref<Array<WeekScheduleUserInterface>>([]);
const absenceReports = ref<Array<ReportInterface>>([]);
const publicHolidays = ref<Array<PublicHolidayRangeInterface>>([]);
const publicHolidaysByYear = new Map<string, Array<PublicHolidayRangeInterface>>();
const loadingPublicHolidayYears = new Set<string>();
const pendingRoleExtensions = ref<Array<WeekSchedulePendingRoleExtensionInterface>>([]);
const pageSizeRef = ref<number>(25);
const currentPage = ref<number>(1);
const sortingRef = ref<object>({});
const viewMode = ref<"timeline" | "table">("table");
const pendingRoleExtensionRequestLimit = 4;

// Lifecycle hooks
onBeforeMount(() => {
  bookmarkedFiltersStore.getUserBookmarkedFilters(profile.value?.id as number);
  getSchedulePagefromURL();
});

// Component methods
const getSchedulePagefromURL = (url: any = $route?.query) => {
  const { page, filters, sorting, pageSize } = qsDecode(url);

  pageSizeRef.value = loadTablePageSize();

  if (page) currentPage.value = parseInt(page);
  if (filters) filtersRef.value = filters;
  if (sorting) sortingRef.value = sorting;
  if (pageSize) pageSizeRef.value = parseInt(pageSize);
  if (!filters) {
    filtersRef.value.min_workload = 20;
  }

  getSchedulePage({ page: currentPage.value });
};

const getSchedulePage = ({
  page,
  pageSize = pageSizeRef.value,
  filters = filtersRef.value,
  sorting = sortingRef.value,
}: {
  page: any;
  pageSize?: number;
  filters?: any;
  sorting?: object;
}): void => {
  loadingTable.value = true;
  currentPage.value = page;
  pageSizeRef.value = pageSize;

  syncRouteParams(
    {
      page: currentPage.value,
      pageSize: pageSizeRef.value,
      filters: filtersRef.value,
      sorting: sortingRef.value,
    },
    instance
  );
  persistTablePageSize(pageSize);
  weekScheduleStore
    .getScheduleByPage({
      page,
      page_size: pageSize,
      ...{
        ...filters,
        min_workload: (filters.min_workload || 0) / 100,
      },
      ...sorting,
    })
    .then(async (results: Array<WeekScheduleUserInterface>) => {
      const [reports, roleExtensions] = await Promise.all([
        getAbsenceReports(results),
        getPendingRoleExtensions(results),
      ]);
      schedulePage.value = results;
      absenceReports.value = reports;
      pendingRoleExtensions.value = roleExtensions;
    })
    .finally(() => {
      loadingTable.value = false;
    });
};

const getAbsenceReports = async (
  users: Array<WeekScheduleUserInterface>
): Promise<Array<ReportInterface>> => {
  const userIds = users.map((user) => user.id);
  if (userIds.length === 0) return [];

  try {
    return await apiGetAbsenceReportsAsync({
      user_id: userIds,
      start_date: moment()
        .startOf("isoWeek")
        .subtract(1, "year")
        .startOf("isoWeek")
        .format("YYYY-MM-DD"),
      sort: "start_date",
      order: "asc",
    });
  } catch {
    return [];
  }
};

const getPublicHolidays = async (years: Array<string>): Promise<void> => {
  const yearsToLoad = years.filter(
    (year) => !publicHolidaysByYear.has(year) && !loadingPublicHolidayYears.has(year)
  );
  yearsToLoad.forEach((year) => loadingPublicHolidayYears.add(year));

  await Promise.all(
    yearsToLoad.map(async (year) => {
      try {
        const response = await apiGetAnnualPublicHolidays(year);
        publicHolidaysByYear.set(year, response.data.data as Array<PublicHolidayRangeInterface>);
      } catch {
        publicHolidaysByYear.set(year, []);
      } finally {
        loadingPublicHolidayYears.delete(year);
      }
    })
  );

  publicHolidays.value = [...publicHolidaysByYear.values()].flat();
};

const getPendingRoleExtensions = async (
  users: Array<WeekScheduleUserInterface>
): Promise<Array<WeekSchedulePendingRoleExtensionInterface>> => {
  const usersWithPendingExtensions = users.filter((user) => user.nb_pending_extensions > 0);
  const extensions: Array<WeekSchedulePendingRoleExtensionInterface> = [];

  for (
    let startIndex = 0;
    startIndex < usersWithPendingExtensions.length;
    startIndex += pendingRoleExtensionRequestLimit
  ) {
    const usersBatch = usersWithPendingExtensions.slice(
      startIndex,
      startIndex + pendingRoleExtensionRequestLimit
    );
    const batchExtensions = await Promise.all(
      usersBatch.map(async (user) => {
        try {
          const response = (await apiGetRoleExtensions({
            role_user_id: user.id,
            pending: true,
            page: 1,
            page_size: user.nb_pending_extensions,
          })) as { data: { data: Array<RoleExtensionInterface> } };

          return response.data.data
            .filter(
              (extension: RoleExtensionInterface) =>
                extension.pending === true &&
                !!extension.end_date &&
                !!extension.role?.end_date &&
                !!extension.role?.project
            )
            .map<WeekSchedulePendingRoleExtensionInterface>(
              (extension: RoleExtensionInterface) => ({
                id: extension.id,
                user_id: user.id,
                pending: extension.pending === true,
                status:
                  extension.colleague_accepted === true
                    ? "approved"
                    : extension.colleague_accepted === false
                      ? "to_be_discussed"
                      : "waiting",
                end_date: extension.end_date,
                workload: extension.workload,
                role: {
                  id: extension.role!.id,
                  title: extension.role!.title,
                  end_date: extension.role!.end_date,
                  project: {
                    id: extension.role!.project.id,
                    name: extension.role!.project.name,
                  },
                },
              })
            );
        } catch {
          return [];
        }
      })
    );

    extensions.push(...batchExtensions.flat());
  }

  return extensions;
};

const applyFilters = (filters: object): void => {
  filtersRef.value = filters;
  currentPage.value = 1;
  syncRouteParams(
    {
      page: currentPage.value,
      pageSize: pageSizeRef.value,
      filters: filtersRef.value,
      sorting: sortingRef.value,
    },
    instance
  );
  getSchedulePage({ page: currentPage.value });
};

const applySorting = (sorting: object): void => {
  sortingRef.value = sorting;

  syncRouteParams(
    {
      page: currentPage.value,
      pageSize: pageSizeRef.value,
      filters: filtersRef.value,
      sorting: sortingRef.value,
    },
    instance
  );
  getSchedulePage({ page: currentPage.value });
};
</script>
